import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'
import { copyToClipboard, getCurrentIanaTimezone } from '../../utils'
import { useEffect } from 'react'
import { ScheduledSessionOfUser } from '../../AppContext'
import { ApiCreateRoomResponse } from '../../RoomContext'
import { ExternalEventEmitter } from '../../ExternalEventEmitter'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import { Alert } from '@/UIComponents2/Alert'
import { Button } from '@/UIComponents2/Button'
import { frontpageReloadTrigger } from '../DashboardPage/internal/useFrontpage'

export default function ScheduleSessionSuccessPage({
    title,
    postResult,
    onClose,
}: {
    title: string
    postResult: ApiCreateRoomResponse | { room: ScheduledSessionOfUser }
    onClose: () => void
}) {
    const { t } = useTranslation()

    const hostUsername = postResult?.room?.host?.username || ''
    const roomName = postResult?.room?.name || ''
    const scheduledStartTime = postResult?.room?.scheduledStartTime
    const scheduledEndTime = postResult?.room?.scheduledEndTime
    const joinLink = postResult?.room?.joinLink
    const joinCode = postResult?.room?.joinCode
    const password = postResult?.room?.password || null

    const collaboratorsNotFound = 'collaboratorsNotFound' in postResult ? postResult?.collaboratorsNotFound || [] : []

    const scheduledTimeStr =
        `${moment(scheduledStartTime).format('L')} ` +
        `${moment(scheduledStartTime).format('h:mmA')} - ` +
        `${moment(scheduledEndTime).format('h:mmA')} ` +
        `${moment.tz(getCurrentIanaTimezone()).format('z')}`

    const onOpenSession = () => {
        ExternalEventEmitter.set({
            eventName: 'join-room-code',
            value: joinCode,
        })
        onClose()
    }

    const onCopyInvitation = () => {
        const invitationText = `Host: ${hostUsername}

Session name: ${roomName}

Time: ${scheduledTimeStr}

Session link:

${joinLink}`

        copyToClipboard(invitationText)
    }

    useEffect(() => {
        frontpageReloadTrigger.trigger()
    }, [])

    return (
        <div className="w-full">
            <DialogBody>
                <DialogTitle>{title}</DialogTitle>

                <div className="flex flex-col">
                    <div className="flex flex-col gap-1">
                        <div>
                            Host: <span className="text-muse-yellow">{hostUsername}</span>
                        </div>
                        <div>
                            Session name: <span className="text-muse-yellow">{roomName}</span>
                        </div>
                        <div>
                            Time: <span className="text-muse-yellow">{scheduledTimeStr}</span>
                        </div>
                        <div>
                            <div>Session link:</div>
                            <span className="text-muse-yellow whitespace-nowrap">{joinLink}</span>
                        </div>

                        {/* only show password if password protected */}
                        {password && (
                            <div>
                                <div>Password:</div>
                                <div>
                                    <span className="text-muse-yellow">{password}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {collaboratorsNotFound.length > 0 && (
                        <div className="my-4">
                            <Alert variant="solid" level="warning">
                                <div className="flex flex-col">
                                    <span className="mb-2">
                                        {`Failed to invite ${collaboratorsNotFound.length} user(s) because` +
                                            ` they do not exist on our system: ${collaboratorsNotFound.join(', ')}.`}
                                    </span>
                                    <span>
                                        You can update the list of invited users in the Scheduled sessions panel.
                                    </span>
                                </div>
                            </Alert>
                        </div>
                    )}
                </div>
            </DialogBody>

            <div className="flex pt-10">
                <div className="flex grow">
                    <Button variant="secondary" onClick={onOpenSession}>
                        {t('Open Session')}
                    </Button>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={onClose}>
                        {t('Close')}
                    </Button>
                    <Button variant="primary" onClick={onCopyInvitation}>
                        {t('Copy invitation')}
                    </Button>
                </div>
            </div>
        </div>
    )
}
